import React from 'react'

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import {
    IconButton,
    Menu,
    MenuItem,
    Typography,
    useTheme,
} from '@material-ui/core'

import {
    MoreVert as MoreIcon,
    ExpandMore as ExpandMoreIcon,
    ExitToApp as ExitToAppIcon,
    Tune as TuneIcon,
} from '@material-ui/icons'

import { AppSelector, AvatarCircle, Container } from '../../../components'
import { User } from '../../../models'

import { Routes } from '../../config/routes'
import { DemoDataMenuItem } from '../../DemoDataMenuItem'
import { useHistory } from 'react-router-dom'
import { useUser } from '../../../hooks'
import { useOrganizationSummary } from '../../../hooks/useSelfServeEntrataOnboard'

import { TOP_BAR_HEIGHT, getSidebarWidth } from '../../constants'
import SideBar from '../AuthLayout/sidebar'
import OrganizationSelection from '../TopBar/OrganizationSelection'
import { UserTagsProvider } from '../../../contexts/UserTagsContext'
import {
    useVendorInvoiceState,
    VendorInvoiceContext,
} from '../../../contexts/useVendorInvoice'

interface OrganizationLayoutProps {
    children: React.ReactNode
    user: User
}

export const OrganizationLayout: React.FC<OrganizationLayoutProps> = ({
    children,
    user,
}) => {
    const theme = useTheme()

    const history = useHistory()

    const { workspaceUser, organization, rootUser, actions } = useUser()
    useOrganizationSummary(organization)

    const vendorInvoiceState = useVendorInvoiceState()

    const sidebarWidth = getSidebarWidth(
        workspaceUser?.active_workspace.company_type,
    )

    const useStyles = makeStyles<Theme, { sidebarWidth: number }>((theme) =>
        createStyles({
            root: {
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                width: '100vw',
            },

            content: {
                flexGrow: 1,
                width: ({ sidebarWidth }) => `calc(100vw - ${sidebarWidth}px)`,
                height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
            },
            topBar: {
                backgroundColor: theme.palette.primary.dark,
                minHeight: TOP_BAR_HEIGHT,
                maxHeight: TOP_BAR_HEIGHT,
                width: '100%',
                color: theme.palette.white.main,
                alignItems: 'center',
            },
            sectionDesktop: {
                display: 'none',
                [theme.breakpoints.up('md')]: {
                    display: 'flex',
                },
            },
            sectionMobile: {
                display: 'flex',
                [theme.breakpoints.up('md')]: {
                    display: 'none',
                },
            },
            profilePic: {
                display: 'flex',
            },
            menuItem: {
                '&:hover': {
                    backgroundColor: theme.palette.grey[300],
                },
            },
        }),
    )
    const classes = useStyles({ sidebarWidth })

    const { logout } = actions

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

    const [
        mobileMoreAnchorEl,
        setMobileMoreAnchorEl,
    ] = React.useState<null | HTMLElement>(null)

    const isMenuOpen = Boolean(anchorEl)
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl)

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        handleMobileMenuClose()
    }

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMoreAnchorEl(event.currentTarget)
    }

    // TODO: Add organization settings and reports
    const menuId = 'primary-search-account-menu'
    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            id={menuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}
            style={{ marginTop: '40px' }}
        >
            <MenuItem
                onClick={() => {
                    handleMenuClose()
                    history.push(Routes.organization.configurations)
                }}
                className={classes.menuItem}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <TuneIcon style={{ marginRight: '8px' }} />
                Configurations
            </MenuItem>
            <DemoDataMenuItem
                className={classes.menuItem}
                onClose={handleMenuClose}
            />
            <MenuItem
                onClick={() => {
                    logout()
                    history.push(Routes.public.login)
                }}
                className={classes.menuItem}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <ExitToAppIcon style={{ marginRight: '8px' }} />
                Logout
            </MenuItem>
        </Menu>
    )

    const mobileMenuId = 'primary-search-account-menu-mobile'
    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            <MenuItem onClick={handleProfileMenuOpen}>
                <IconButton
                    aria-label="account of current user"
                    aria-controls="primary-search-account-menu"
                    aria-haspopup="true"
                    color="inherit"
                >
                    <AvatarCircle
                        text={rootUser?.name}
                        initials
                        image={rootUser?.profile_picture}
                        styles={{
                            container: {
                                maxHeight: '30px',
                                maxWidth: '30px',
                                minHeight: 'auto',
                                minWidth: 'auto',
                                fontSize: '1rem',
                                padding: '16px',
                            },
                        }}
                    />
                    {/* <AccountCircle /> */}
                </IconButton>
                <p>Profile</p>
            </MenuItem>
            <MenuItem
                onClick={() => {
                    handleMobileMenuClose()
                    history.push(Routes.organization.configurations)
                }}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <TuneIcon style={{ marginRight: '8px' }} />
                Configurations
            </MenuItem>
            <DemoDataMenuItem onClose={handleMobileMenuClose} />
        </Menu>
    )

    return (
        <UserTagsProvider>
            <Container style={{ height: '100vh', width: '100vw' }}>
                {/* Left Side Bar */}
                {/* <OrganizationSideBar open drawerWidth={DRAWER_WIDTH} /> */}

                <SideBar
                    open={true}
                    drawerWidth={sidebarWidth}
                    selectedChannel="none"
                    setSelectedChannel={() => {}}
                />

                {/* Right Panel, top bar and content */}
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    <Container
                        className={classes.topBar}
                        style={{ padding: theme.spacing(2) }}
                    >
                        <OrganizationSelection
                            user={user}
                            selectedOrganization={organization}
                        />

                        <Container style={{ flex: 1 }} />

                        <AppSelector
                            workspaceUser={workspaceUser}
                            theme={theme}
                        />

                        <div className={classes.sectionDesktop}>
                            {/* profile icon */}
                            <IconButton
                                edge="end"
                                aria-label="account of current user"
                                aria-controls={menuId}
                                aria-haspopup="true"
                                onClick={handleProfileMenuOpen}
                                color="inherit"
                                style={{ marginRight: theme.spacing(0.5) }}
                            >
                                <Typography style={{ marginRight: '5px' }}>
                                    {rootUser?.name}
                                </Typography>
                                <AvatarCircle
                                    text={rootUser?.name}
                                    initials
                                    image={rootUser?.profile_picture}
                                    styles={{
                                        container: {
                                            maxHeight: 45,
                                            maxWidth: 45,
                                            minHeight: 'auto',
                                            minWidth: 'auto',
                                            height: '35px',
                                            width: '35px',
                                            fontSize: '1rem',
                                        },
                                    }}
                                />
                                <ExpandMoreIcon style={{ marginLeft: '5px' }} />
                            </IconButton>
                        </div>
                        <div className={classes.sectionMobile}>
                            <IconButton
                                aria-label="show more"
                                aria-controls={mobileMenuId}
                                aria-haspopup="true"
                                onClick={handleMobileMenuOpen}
                                color="inherit"
                            >
                                <MoreIcon />
                            </IconButton>
                        </div>
                    </Container>

                    {renderMobileMenu}
                    {renderMenu}

                    {/* Content */}
                    <main
                        className={classes.content}
                        style={{ padding: '24px' }}
                    >
                        <VendorInvoiceContext.Provider
                            value={vendorInvoiceState}
                        >
                            {children}
                        </VendorInvoiceContext.Provider>
                    </main>
                </Container>
            </Container>
        </UserTagsProvider>
    )
}
